/**
 * @fileoverview This file defines functions for interacting with various Large Language Model (LLM) APIs, supporting streaming chat completions, tool calls, and model selection. It provides a unified interface for working with different AI models like GPT, Claude, and Gemini, with features for usage tracking, logging, and dry run mode.
 */

import { Message, Model, ResponseToolCall } from '@iterator/shared-node/ai/llm_types'
import { allInputTools, doToolCalls } from '@src/tools/all_tools'
import { trackUsage } from '@iterator/shared-node/util/usage_tracker'
import { print_ai, startThinkingAnimation } from '@iterator/shared-node/util/logging'
import { ClaudeHaikuModel, DefaultModel } from './models'

export async function streamChatCompletions(
  system: string,
  texts: string[],
  conversation: Message[],
  onChunk: (chunk: string) => void,
  model: Model = ClaudeHaikuModel,
  prediction?: string,
): Promise<{ message: Message }> {
  if (process.env.DRY_RUN === 'true') {
    print_ai('Prompts provided:', JSON.stringify({ system, texts, conversation }, null, 2))
    onChunk('Dry run mode: No actual output generated')
    return { message: { content: 'Dry run mode: No actual output generated', role: 'assistant' } }
  }

  const stopThinking = startThinkingAnimation()
  let hasStoppedThinking = false

  try {
    const response = await model.getApi().streamChatCompletions(
      system,
      texts,
      conversation,
      (chunk) => {
        if (!hasStoppedThinking) {
          stopThinking()
          hasStoppedThinking = true
        }
        onChunk(chunk)
      },
      prediction,
    )

    trackUsage('streamChatCompletions', response.usage, model)

    return { message: response.message }
  } finally {
    if (!hasStoppedThinking) {
      stopThinking()
    }
  }
}

export async function callChatCompletionsForToolCalls(
  system: string,
  texts: string[],
  conversation: Message[],
  model: Model = DefaultModel,
): Promise<{ toolCalls: ResponseToolCall[]; message: Message }> {
  if (process.env.DRY_RUN === 'true') {
    print_ai('Prompts provided:', JSON.stringify({ system, texts, conversation }, null, 2))
    print_ai('Dry run mode: No actual output generated')
    return {
      toolCalls: [],
      message: { content: 'Dry run mode: No actual output generated', role: 'assistant' },
    }
  }

  const response = await model.getApi().chatCompletion(system, texts, allInputTools(), conversation)
  trackUsage('callChatCompletionsForToolCalls', response.usage, model)

  return { toolCalls: response.toolCalls, message: response.message }
}

export async function callChatCompletionsWithToolsAndExecuteTools(
  system: string,
  texts: string[],
  conversation: Message[],
  model: Model = DefaultModel,
  thinkingMessage?: string,
): Promise<{ modelResponse: string; toolResponses: string[] }> {
  if (process.env.DRY_RUN === 'true') {
    print_ai('Prompts provided:', JSON.stringify({ system, texts, conversation }, null, 2))
    return { modelResponse: 'Dry run mode: No actual output generated', toolResponses: [] }
  }

  const stopThinking = startThinkingAnimation(thinkingMessage)

  const response = await model.getApi().chatCompletion(system, texts, allInputTools(), conversation)
  stopThinking()

  const output = response.message.content
  const toolResponses = await doToolCalls(response.toolCalls)
  trackUsage('callChatCompletionsWithToolsAndExecuteTools', response.usage, model)
  return { modelResponse: output, toolResponses }
}
